import * as React from 'react';
import {Link} from 'react-router-dom';
// @ts-expect-error
import type {LocationShape} from 'react-router-dom';

import type {SavedFilter} from 'nutshell-core/types';

import {Dialog} from 'shells/dialog/dialog';
import {LinkedEntity} from 'shells/linked-entity/linked-entity';
import {ListRow} from 'shells/list-row/list-row';
import {ListRowWithIcon} from 'shells/list-row/list-row-with-icon';

import styles from './saved-filter-modal-row.module.css';

type Props = {
    savedFilter: SavedFilter<any>;
    subtitle: string | React.ReactElement<any>;
    location: LocationShape;
    icon: React.ComponentType<any>;
    isSelected: boolean;
    onSelect: (arg1: SavedFilter<any>) => any;
    onDelete: (arg1: string) => any;
};

type State = {
    isDeletionDialogOpen: boolean;
};

export class SavedFilterModalRow extends React.Component<Props, State> {
    row: HTMLElement | null | undefined;

    constructor() {
        // @ts-expect-error
        super();
        this.state = {
            isDeletionDialogOpen: false,
        };
    }

    render() {
        const options = [];
        const {savedFilter} = this.props;

        if (savedFilter.isDeleteable) {
            options.push({
                label: 'Delete',
                handleSelect: this.handleOpenDeletionDialog,
            });
        }

        return (
            // @ts-expect-error
            <Link to={this.props.location} className='inline-block-link'>
                <ListRow
                    onClick={this.handleRowClick}
                    hasBorders={true}
                    isSelected={this.props.isSelected}
                    options={options}
                >
                    <div
                        ref={(node) => {
                            this.row = node;
                        }}
                        className={styles['row-container']}
                    >
                        <ListRowWithIcon
                            title={savedFilter.name}
                            subtitle={this.props.subtitle}
                            icon={this.props.icon}
                            createdTime={
                                !savedFilter.isImmutable && savedFilter.createdTime
                                    ? savedFilter.createdTime
                                    : undefined
                            }
                        />
                    </div>
                    {this.state.isDeletionDialogOpen && (
                        <Dialog
                            anchor={this.row}
                            location='top'
                            question={`Are you sure you want to delete ${savedFilter.name}?`}
                            onPrimary={this.handleDelete}
                            onSecondary={this.handleCloseDeletionDialog}
                        />
                    )}
                </ListRow>
            </Link>
        );
    }

    handleOpenDeletionDialog = () => {
        this.setState(() => ({isDeletionDialogOpen: true}));
    };

    handleCloseDeletionDialog = () => {
        this.setState(() => ({isDeletionDialogOpen: false}));
    };

    handleDelete = () => {
        this.props.onDelete(this.props.savedFilter.id);
        this.handleCloseDeletionDialog();
    };

    handleRowClick = () => {
        this.props.onSelect(this.props.savedFilter);
    };
}

/**
 * Generate the second row of a saved filter row, using some text to explain what type
 * of saved filter this is (generally the report type or entity list type), and if
 * it is shared, who shared it.
 * @param {Object} savedFilter     - The saved filter object being rendered
 * @param {string} savedFilterType - The type of saved filter, this will go right after the icon
 * @param {string} [sessionUserId] - If provided, we'll try to be clever and use "shared by you"
 * @returns {ReactElement}         - JSX to render on the second line of a saved filter modal row
 */
export const getSavedFilterSubtitle = (
    savedFilter: SavedFilter<any>,
    savedFilterType: string,
    sessionUserId?: string | null
): React.ReactElement<any> => {
    if (!savedFilter.isShared) {
        return <span>{savedFilterType}</span>;
    }

    const creatorName = getSharingUserDisplayText(savedFilter.links.user, sessionUserId);

    return (
        <div className={styles['shared-by']}>
            {savedFilterType}shared by {creatorName}
        </div>
    );
};

/**
 * Determines what name to display when showing who shared a report. If the
 * sharing user is the logged in user, it will display the text 'you',
 * otherwise it will look up the name of the user and show a LinkedEntity
 * rendering of it.
 * @param {string} sharingUserId The ID of the person who shared the report.
 * @param {string} sessionUserId The ID of the logged in user.
 * @return {LinkedEntity|string} A raw string or a LinkedEntity component.
 */
function getSharingUserDisplayText(
    sharingUserId: string,
    sessionUserId?: string | null
): React.ReactElement<any> | string {
    if (sharingUserId === sessionUserId) {
        return 'you';
    }

    return getSharedWithComponentForId(sharingUserId) || '…';
}

function getSharedWithComponentForId(id: string): React.ReactElement<any> | null | undefined {
    const model = Nut.Model.getModelById(id);
    if (model) {
        return <LinkedEntity type={model.type} name={model.get('name')} isLinkable={false} />;
    }

    return undefined;
}
